import type { Polygon, Feature, MultiPolygon, LineString, Position } from "geojson"
import { Layer, Source } from "react-map-gl/mapbox"
import type { Expression } from "mapbox-gl"
import type { LayerProps } from "react-map-gl/mapbox"

import type { UseConstructionStudyReturn } from "@/hooks/explore/use-construction-study"

// Calculate distance between two points (Haversine)
const calculateDistance = (coord1: Position, coord2: Position): number => {
  const R = 6371000
  const lat1 = (coord1[1] * Math.PI) / 180
  const lat2 = (coord2[1] * Math.PI) / 180
  const deltaLat = ((coord2[1] - coord1[1]) * Math.PI) / 180
  const deltaLon = ((coord2[0] - coord1[0]) * Math.PI) / 180
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const getSegmentMidpoints = (polygons: Feature<MultiPolygon | Polygon>[]): Feature<LineString>[] => {
  const segments: Feature<LineString>[] = []
  polygons.forEach((polygon, polygonIndex) => {
    const coordinates =
      polygon.geometry.type === "Polygon" ? polygon.geometry.coordinates : polygon.geometry.coordinates[0]
    coordinates.forEach((ring) => {
      for (let i = 0; i < ring.length - 1; i++) {
        const start = ring[i]
        const end = ring[i + 1]
        const distance = calculateDistance(start, end)
        if (distance < 1) continue
        const midpoint: Position = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
        const distanceText =
          distance < 1000 ? `${Math.round(distance * 10) / 10}m` : `${Math.round(distance / 100) / 10}km`
        segments.push({
          type: "Feature",
          properties: {
            distance: distanceText,
            distanceValue: distance,
            polygonIndex,
          },
          geometry: { type: "LineString", coordinates: [midpoint, midpoint] },
        })
      }
    })
  })
  return segments
}

interface StudyMapContentProps {
  study: UseConstructionStudyReturn
}

const PRIMARY_COLOR = "#1E40AF"
const ERROR_COLOR = "#ef4444"
const TEXT_COLOR = "#1f2937"

export const StudyMapContent = ({ study }: StudyMapContentProps) => {
  const { finalPolygons, forbiddenPolygons, basePolygon, displayMode, isEditing } = study

  const finalPolygonMidpoints = !isEditing ? getSegmentMidpoints(finalPolygons) : []
  const basePolygonMidpoints = !isEditing && basePolygon ? getSegmentMidpoints([basePolygon]) : []
  const forbiddenPolygonMidpoints = !isEditing ? getSegmentMidpoints(forbiddenPolygons) : []

  const extrudeLayer: LayerProps = {
    type: "fill-extrusion",
    paint: {
      "fill-extrusion-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        PRIMARY_COLOR,
        ["get", "color"],
      ] as Expression,
      "fill-extrusion-height":
        displayMode === "2D" ? 0 : (["interpolate", ["linear"], ["zoom"], 15, 0, 18, ["get", "height"]] as Expression),
      "fill-extrusion-opacity": 0.9,
    },
    id: "",
  }

  return (
    <>
      {/* Study layers */}
      <Source
        id="forbidden-polygons-source"
        type="geojson"
        promoteId={"uniqid"}
        data={{ type: "FeatureCollection", features: forbiddenPolygons }}
      >
        <Layer
          id="forbidden-fill"
          type="fill"
          paint={{
            "fill-color": ERROR_COLOR,
            "fill-opacity": 0.4,
          }}
        />
      </Source>

      {basePolygon && (
        <Source id="concave-hull" type="geojson" data={basePolygon}>
          <Layer {...extrudeLayer} id="extrude-base" />
          <Layer
            id="border-hull"
            type="line"
            paint={{
              "line-color": PRIMARY_COLOR,
              "line-width": 2,
            }}
          />
        </Source>
      )}

      {!isEditing && (
        <Source
          promoteId={"uniqid"}
          id="custom-polygons-source"
          type="geojson"
          data={{ type: "FeatureCollection", features: finalPolygons }}
        >
          <Layer {...extrudeLayer} id="custom-polygons" />
          <Layer
            id="border-custom"
            type="line"
            paint={{
              "line-color": PRIMARY_COLOR,
              "line-width": 2,
            }}
          />
        </Source>
      )}

      {!isEditing && finalPolygonMidpoints.length > 0 && (
        <Source
          id="segment-distances-source"
          type="geojson"
          data={{ type: "FeatureCollection", features: finalPolygonMidpoints }}
        >
          <Layer
            id="segment-distance-labels"
            type="symbol"
            layout={{
              "text-field": ["get", "distance"],
              "text-font": ["Open Sans Bold"],
              "text-size": 12,
              "text-anchor": "center",
              "symbol-placement": "point",
            }}
            paint={{
              "text-color": TEXT_COLOR,
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
            }}
          />
        </Source>
      )}

      {!isEditing && basePolygonMidpoints.length > 0 && (
        <Source
          id="base-segment-distances-source"
          type="geojson"
          data={{ type: "FeatureCollection", features: basePolygonMidpoints }}
        >
          <Layer
            id="base-segment-distance-labels"
            type="symbol"
            layout={{
              "text-field": ["get", "distance"],
              "text-font": ["Open Sans Bold"],
              "text-size": 12,
              "text-anchor": "center",
              "symbol-placement": "point",
            }}
            paint={{
              "text-color": TEXT_COLOR,
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
            }}
          />
        </Source>
      )}

      {!isEditing && forbiddenPolygonMidpoints.length > 0 && (
        <Source
          id="forbidden-segment-distances-source"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: forbiddenPolygonMidpoints,
          }}
        >
          <Layer
            id="forbidden-segment-distance-labels"
            type="symbol"
            layout={{
              "text-field": ["get", "distance"],
              "text-font": ["Open Sans Bold"],
              "text-size": 12,
              "text-anchor": "center",
              "symbol-placement": "point",
            }}
            paint={{
              "text-color": ERROR_COLOR,
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
            }}
          />
        </Source>
      )}
    </>
  )
}
